import React, { useMemo } from 'react';
import { Button } from '../../ui/button';
import FundingCopyableField from './FundingCopyableField';
import {
  formatDateField,
  formatMoneyField,
  resolveClientIntakeDisplay,
} from '../../../utils/fundingClientIntakeDisplay';
import { getDevMockSsnFull, isFrontendDevAuthBypassEnabled } from '../../../utils/devAuth';
import {
  revealClientApplicationPassword,
  revealClientEin,
  revealClientSsn,
} from '../../../services/funding/fundingClientsService';

const InfoCard = ({
  index,
  title,
  desc,
  children,
  testId,
  wide = false,
  fieldsClassName = 'fg-client-info-fields',
}) => (
  <article
    className={`fg-client-info-card${wide ? ' fg-client-info-card--wide' : ''}`}
    data-testid={testId}
  >
    <div className="fg-client-info-card__header">
      <span className="fg-client-info-card__index">{index}</span>
      <div>
        <h3>{title}</h3>
        {desc ? <p>{desc}</p> : null}
      </div>
    </div>
    <div className={fieldsClassName}>{children}</div>
  </article>
);

const Field = (props) => <FundingCopyableField {...props} />;

const FundingClientIntakeProfilePanel = ({
  client,
  clientId,
  agencyId,
  onEdit,
}) => {
  const data = useMemo(() => resolveClientIntakeDisplay(client), [client]);

  const revealSsnValue = async () => {
    if (!clientId || !agencyId) return null;
    if (isFrontendDevAuthBypassEnabled()) {
      return getDevMockSsnFull(data.ssnLast4);
    }
    return revealClientSsn(clientId, agencyId);
  };

  const revealEinValue = async () => {
    if (!clientId || !agencyId) return null;
    return revealClientEin(clientId, agencyId);
  };

  const revealPasswordValue = async () => {
    if (!clientId || !agencyId || !data.hasApplicationPasswordCreated) return null;
    return revealClientApplicationPassword(clientId, agencyId);
  };

  const revealDob = async () => data.dob || null;

  const ssnMasked = data.ssnMasked || (data.ssnLast4 ? `***-**-${data.ssnLast4}` : '');
  const einMasked = data.einMasked || (data.einLast4 ? `**-***${data.einLast4}` : '');
  const passwordMasked = data.hasApplicationPasswordCreated
    ? (data.applicationPasswordCreatedMasked || '••••••••')
    : '';

  return (
    <section className="fg-client-info-panel" data-testid="client-intake-profile-panel">
      <div className="fg-client-info-header">
        <div>
          <p className="fg-eyebrow">Client Info</p>
          <h2>Application-ready profile</h2>
          <p>Copy, reveal, and edit the client fields used for lender submissions.</p>
        </div>
        {onEdit ? (
          <Button
            className="rounded-xl bg-[var(--fg-accent)] hover:bg-[#126b4f]"
            data-testid="client-info-edit-btn"
            onClick={() => onEdit(client)}
          >
            Edit Client Info
          </Button>
        ) : null}
      </div>

      <div className="fg-client-info-grid">
        <InfoCard index={1} title="Client Contact" desc="Basic contact information." testId="intake-section-contact">
          <Field label="First Name" value={data.firstName} testId="intake-first-name" />
          <Field label="Middle Name" value={data.middleName} testId="intake-middle-name" />
          <Field label="Last Name" value={data.lastName} testId="intake-last-name" />
          <Field label="Full Name" value={data.fullName === '—' ? '' : data.fullName} testId="intake-full-name" />
          <Field label="Email" value={data.email} testId="intake-email" />
          <Field label="Phone" value={data.phone} testId="intake-phone" />
          <Field label="Status" value={data.status} testId="intake-status" />
          <Field label="Source" value={data.source} testId="intake-source" />
        </InfoCard>

        <InfoCard index={2} title="Identity Profile" desc="Personal identity used on applications." testId="intake-section-identity">
          <Field label="Ownership %" value={data.ownership} testId="intake-ownership" />
          <Field
            label="SSN"
            maskedValue={ssnMasked}
            isSensitive
            canReveal={Boolean(data.ssnLast4 || data.hasClientSsn)}
            onReveal={revealSsnValue}
            copyRequiresReveal
            revealErrorMessage="Unable to reveal SSN"
            testId="intake-ssn"
            monospace
          />
          <Field
            label="DOB"
            value={data.dob}
            displayValue={formatDateField(data.dob)}
            maskedValue={data.dobMasked}
            isSensitive
            canReveal={Boolean(data.dob)}
            onReveal={revealDob}
            copyValue={data.dob}
            copyRequiresReveal
            testId="intake-dob"
          />
          <Field label="Personal Address" value={data.personalAddress} testId="intake-personal-address" />
          <Field label="Time Live in House" value={data.timeLiveInHouse} testId="intake-time-live" />
          <Field
            label="Housing Payment"
            value={data.housingPayment}
            displayValue={formatMoneyField(data.housingPayment)}
            testId="intake-housing-payment"
          />
        </InfoCard>

        <InfoCard index={3} title="Application Access" desc="Credentials created for lender/application submissions." testId="intake-section-application-access">
          <Field label="Application Email Created" value={data.applicationEmailCreated} testId="intake-application-email" />
          <Field
            label="Application Password Created"
            maskedValue={passwordMasked}
            isSensitive
            canReveal={data.hasApplicationPasswordCreated}
            onReveal={revealPasswordValue}
            copyRequiresReveal
            revealErrorMessage="Unable to reveal password"
            helperText="Credentials for lender portal submissions — not the client's personal login."
            testId="intake-application-password"
          />
        </InfoCard>

        <InfoCard index={4} title="Business Profile" desc="Business identity and operating details." testId="intake-section-business">
          <Field label="Business Legal Name" value={data.businessLegalName} testId="intake-business-name" />
          <Field
            label="EIN"
            maskedValue={einMasked}
            isSensitive
            canReveal={Boolean(data.einLast4 || data.hasClientEin)}
            onReveal={revealEinValue}
            copyRequiresReveal
            revealErrorMessage="Unable to reveal EIN"
            testId="intake-ein"
            monospace
          />
          <Field label="Business Start Date" value={data.businessStartDate} displayValue={formatDateField(data.businessStartDate)} testId="intake-business-start" />
          <Field label="Business Address" value={data.businessAddress} testId="intake-business-address" />
          <Field label="Business Phone" value={data.businessPhone} testId="intake-business-phone" />
          <Field label="Business Email" value={data.businessEmail} testId="intake-business-email" />
          <Field label="Industry" value={data.industry} testId="intake-industry" />
          <Field label="Entity Type" value={data.entityType} testId="intake-entity-type" />
        </InfoCard>

        <InfoCard index={5} title="Income & Employment" desc="Income, employment, and capacity data." testId="intake-section-income" wide>
          <Field label="Personal Income" value={data.personalIncome} displayValue={formatMoneyField(data.personalIncome)} testId="intake-personal-income" />
          <Field label="Business Income" value={data.businessIncome} displayValue={formatMoneyField(data.businessIncome)} testId="intake-business-income" />
          <Field label="Employer" value={data.employer} testId="intake-employer" />
          <Field label="Time Working At" value={data.timeWorkingAt} testId="intake-time-working" />
          <Field label="Monthly Revenue" value={data.monthlyRevenue} displayValue={formatMoneyField(data.monthlyRevenue)} testId="intake-monthly-revenue" />
          <Field label="Monthly Deposits" value={data.monthlyDeposits} displayValue={formatMoneyField(data.monthlyDeposits)} testId="intake-monthly-deposits" />
          <Field label="Business Expenses" value={data.businessExpenses} displayValue={formatMoneyField(data.businessExpenses)} testId="intake-business-expenses" />
          <Field label="Savings" value={data.savings} displayValue={formatMoneyField(data.savings)} testId="intake-savings" />
          <Field label="Retirement Account" value={data.retirement} displayValue={formatMoneyField(data.retirement)} testId="intake-retirement" />
          <Field label="Bank Name" value={data.bankName} testId="intake-bank-name" />
        </InfoCard>

        <InfoCard index={6} title="Credit Profile" desc="Scores and approval positioning." testId="intake-section-credit">
          <Field label="Experian Score" value={data.experian != null ? String(data.experian) : ''} testId="intake-experian" monospace />
          <Field label="TransUnion Score" value={data.transUnion != null ? String(data.transUnion) : ''} testId="intake-transunion" monospace />
          <Field label="Equifax Score" value={data.equifax != null ? String(data.equifax) : ''} testId="intake-equifax" monospace />
          <Field label="Current Highest Limit" value={data.highestLimit} displayValue={formatMoneyField(data.highestLimit)} testId="intake-highest-limit" />
          <Field label="Open Inquiries" value={data.inquiries != null ? String(data.inquiries) : ''} testId="intake-inquiries" monospace />
          <Field label="Utilization %" value={data.utilization} testId="intake-utilization" />
          <Field label="Derogatory Items" value={data.derogatoryItems != null ? String(data.derogatoryItems) : ''} testId="intake-derogatory" monospace />
          <Field label="Readiness %" value={data.readiness} testId="intake-readiness" />
        </InfoCard>

        <InfoCard index={7} title="Funding Goal" desc="Funding targets and application strategy." testId="intake-section-funding">
          <Field label="Desired Funding Amount" value={data.desiredFunding} displayValue={formatMoneyField(data.desiredFunding)} testId="intake-desired-funding" />
          <Field label="Target Product" value={data.targetProduct} testId="intake-target-product" />
          <Field label="Funding Stage" value={data.fundingStage} testId="intake-funding-stage" />
          <Field label="Application Type" value={data.applicationType} testId="intake-application-type" />
          <Field label="Personal Funding Amount" value={data.personalFundingAmount} displayValue={formatMoneyField(data.personalFundingAmount)} testId="intake-personal-funding" />
          <Field label="0% Credit Card Amount" value={data.zeroPercentAmount} displayValue={formatMoneyField(data.zeroPercentAmount)} testId="intake-zero-percent" />
          <Field label="Business LOC Amount" value={data.businessLocAmount} displayValue={formatMoneyField(data.businessLocAmount)} testId="intake-business-loc" />
          <Field label="Target Lender" value={data.targetLender} testId="intake-target-lender" />
        </InfoCard>

        <InfoCard index={8} title="Notes & Tags" desc="Internal advisor notes and segmentation." testId="intake-section-notes" fieldsClassName="fg-client-info-fields fg-client-info-fields--one">
          <Field label="Tags" value={data.tags} testId="intake-tags" />
          <Field label="Advisor Owner" value={data.advisorOwner} testId="intake-advisor-owner" />
          <Field label="Notes" value={data.notes} testId="intake-notes" />
        </InfoCard>
      </div>
    </section>
  );
};

export default FundingClientIntakeProfilePanel;
